/* eslint-disable */
import {
  apply_check_status_to_row,
  build_manual_workspace_documents,
  create_case_row,
  filter_verification_checks,
  get_check_sort_value,
  get_manual_document_status,
  MANUAL_SECTION_TAB_PRECEDENCE,
  merge_selected_check_group,
  resolve_verification_workspace_mode
} from './manage_verification_helpers';
import {
  build_case_gate_journey,
  resolve_gate_id_from_stage_label
} from '@/services/onboarding_journey_service';
import { tablePaginationComputed } from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';
import { pick_manual_mappings_from_items } from './manage_verification_manual_helpers';

const build_workflow_activity_entries = (selected_case = {}) => (
  (Array.isArray(selected_case?.workflow_statuses) ? selected_case.workflow_statuses : [])
    .map((status) => ({
      id: `workflow-${status?.id || status?.status_name || Math.random()}`,
      title: String(status?.status_name || status?.workflow_status_name || status?.name || 'Workflow event').trim(),
      detail: String(status?.description || status?.status || 'Workflow stage').trim(),
      timestamp: String(status?.updated_at || status?.created_at || '').trim(),
      tone: 'secondary'
    }))
);

const build_verification_activity_entries = (selected_case_checks, get_check_history) => (
  selected_case_checks.flatMap((item) => {
    const base_entry = {
      id: `check-${item?.id || Math.random()}`,
      title: `${item?.check || 'Verification'} ${item?.status ? String(item.status).toLowerCase() : 'recorded'}`,
      detail: item?.impact || item?.source || 'Verification activity',
      timestamp: item?.last_run && item.last_run !== '-' ? item.last_run : '',
      tone: item?.status_tone || 'secondary'
    };
    const history_entries = get_check_history(item).map((entry, index) => ({
      id: `${item?.id || 'history'}-${entry?.id || index}`,
      title: `${item?.check || 'Verification'} ${String(entry?.status || 'updated').toLowerCase()}`,
      detail: entry?.detail || item?.impact || 'Verification activity',
      timestamp: entry?.last_run || '',
      tone: entry?.status_tone || item?.status_tone || 'secondary'
    }));

    return history_entries.length ? history_entries : [base_entry];
  })
);

const collect_review_questions = (form_sections = []) => {
  const questions = [];
  form_sections.forEach((form_section) => {
    (form_section?.sections || []).forEach((section) => {
      (section?.sub_sections || []).forEach((sub_section) => {
        (sub_section?.questions || []).forEach((question) => {
          if (Number(question?.id || 0) > 0) questions.push(question);
        });
      });
    });
  });
  return questions;
};

const normalize_stage_label = (value = '') => String(value || '').trim();
const ENTITY_ROLE_VALUES = new Set(['entity', 'entity user', 'entity_user']);

function normalize_role_value(value = '') {
  return String(value || '').trim().toLowerCase().replace(/[-\s]+/g, ' ');
}

function first_non_empty_text(values = [], fallback = '-') {
  const match = values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
  return match === undefined ? fallback : String(match).trim();
}

function get_session_role() {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem('current_role') || '';
}

function get_gate_transition_log(transition_history = [], gate_id = '') {
  const normalized_gate_id = String(gate_id || '').trim().toLowerCase();
  if (!normalized_gate_id) return null;

  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  return logs
    .filter((log) => resolve_gate_id_from_stage_label(log?.to_status?.name || '') === normalized_gate_id)
    .sort((left, right) => new Date(right?.date_created || 0).getTime() - new Date(left?.date_created || 0).getTime())[0] || null;
}

function build_traversed_workflow_gate_ids(transition_history = []) {
  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  if (!logs.length) return [];

  const ordered_logs = logs.sort((left, right) => (
    new Date(left?.date_created || 0).getTime() - new Date(right?.date_created || 0).getTime()
  ));
  const gate_ids = [];
  const append_gate_id = function append_gate_id(status_name = '') {
    const gate_id = resolve_gate_id_from_stage_label(status_name);
    if (gate_id && !gate_ids.includes(gate_id)) {
      gate_ids.push(gate_id);
    }
  };

  append_gate_id(ordered_logs[0]?.from_status?.name || '');
  ordered_logs.forEach((log) => {
    append_gate_id(log?.to_status?.name || '');
  });
  return gate_ids;
}

function format_history_date(value = '') {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return '-';

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return raw.slice(0, 10) || raw;
}

function format_sidebar_history_date(value = '') {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return '-';

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(parsed).replace(',', ',');
}

const manual_stage_label_from_case = (selected_case = {}) => {
  const labels = [
    selected_case?.workflow_display_stage,
    selected_case?.current_workflow_status,
    selected_case?.stage,
    selected_case?.detail,
    selected_case?.application_status,
    selected_case?.status_name
  ].map(normalize_stage_label).filter(Boolean);
  const manual_label = labels.find((label) => label.toLowerCase().includes('manual verif'));
  return manual_label || 'Manual Verification';
};

function build_selected_case_bound_auto_checks(selected_case = {}) {
  const services = Array.isArray(selected_case?.auto_verification_services)
    ? selected_case.auto_verification_services
    : [];

  return services.map((service) => build_selected_case_bound_auto_check(selected_case, service));
}

function matches_selected_case(item, selected_case, matches_case) {
  return matches_case(item?.case_item, selected_case);
}

function build_selected_case_bound_auto_check(selected_case = {}, service = {}) {
  const service_id = Number(service?.id || service?.service_id || 0);
  const case_id = selected_case?.application_id || selected_case?.id || 'case';
  const service_key = service_id || service?.name || 'service';

  return {
    id: `selected-case-service-${case_id}-${service_key}`,
    check: String(service?.name || service?.service_name || 'Verification check').trim() || 'Verification check',
    case_name: selected_case?.name || 'Onboarded Entity',
    source: String(service?.description || service?.name || 'Backend verification').trim() || 'Backend verification',
    status: 'Not Run',
    status_tone: 'secondary',
    impact: 'Ready to run',
    last_run: '-',
    application_form_id: null,
    service_id: service_id || null,
    case_item: selected_case,
    raw_status: null,
    verification_mode: 'auto',
    is_runnable: true,
    service,
    history: []
  };
}

function build_grouped_automatic_result_cards(checks = [], get_summary) {
  const grouped_checks = new Map();

  checks.forEach((item) => {
    merge_selected_check_group(grouped_checks, item);
  });

  return Array.from(grouped_checks.values())
    .sort((left, right) => get_check_sort_value(right) - get_check_sort_value(left))
    .map((item) => ({
      ...item,
      summary: get_summary(item)
    }));
}

function get_manual_item_type(item = {}) {
  return String(item?.verification_type || '').trim().toLowerCase();
}

function is_bank_statement_manual_item(item = {}) {
  return get_manual_item_type(item) === 'bank_statement';
}

function manual_item_has_saved_run(item = {}) {
  return Boolean(
    Number(item?.run_id || item?.manual_run?.id || item?.manual_run?.run_id || 0)
    || item?.manual_run
  );
}

function manual_item_has_review_shape(item = {}) {
  return Boolean(
    Array.isArray(item?.manual_form_sections) && item.manual_form_sections.length
    || Array.isArray(item?.manual_mappings) && item.manual_mappings.length
    || item?.manual_binding
  );
}

function get_manual_item_run_rank(item = {}) {
  const run_id = Number(item?.run_id || item?.manual_run?.id || item?.manual_run?.run_id || 0);
  if (run_id > 0) return run_id;

  const timestamp = Date.parse(String(
    item?.manual_run?.started_at
    || item?.manual_run?.completed_at
    || item?.last_run
    || ''
  ));
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function pick_latest_manual_run_item(items = []) {
  const run_items = items.filter((item) => manual_item_has_saved_run(item));
  if (!run_items.length) return null;

  return run_items.reduce((latest_item, item) => (
    get_manual_item_run_rank(item) >= get_manual_item_run_rank(latest_item)
      ? item
      : latest_item
  ));
}

function prioritize_bank_statement_items(items = []) {
  const normalized_items = Array.isArray(items) ? items : [];
  const bank_statement_items = normalized_items.filter((item) => is_bank_statement_manual_item(item));
  return bank_statement_items.length ? bank_statement_items : normalized_items;
}

function merge_manual_items(primary_item = null, support_item = null) {
  if (!primary_item) return support_item || null;
  if (!support_item) return primary_item;

  return {
    ...support_item,
    ...primary_item,
    manual_binding: primary_item?.manual_binding || support_item?.manual_binding || null,
    manual_mappings: pick_manual_mappings_from_items(primary_item, support_item),
    manual_form_sections: Array.isArray(primary_item?.manual_form_sections) && primary_item.manual_form_sections.length
      ? primary_item.manual_form_sections
      : (Array.isArray(support_item?.manual_form_sections) ? support_item.manual_form_sections : []),
    field_runs: Array.isArray(primary_item?.field_runs) && primary_item.field_runs.length
      ? primary_item.field_runs
      : (Array.isArray(support_item?.field_runs) ? support_item.field_runs : []),
    bank_statement_verification: primary_item?.bank_statement_verification
      || primary_item?.manual_run?.bank_statement_verification
      || support_item?.bank_statement_verification
      || support_item?.manual_run?.bank_statement_verification
      || null,
    manual_run: primary_item?.manual_run || support_item?.manual_run || null
  };
}

function build_manual_workspace_item(manual_items = []) {
  const items = prioritize_bank_statement_items(manual_items);
  if (!items.length) return null;

  const run_backed_item = pick_latest_manual_run_item(items);
  const matching_support_item = run_backed_item
    ? items.find((item) => (
      item !== run_backed_item
      && manual_item_has_review_shape(item)
      && (
        !get_manual_item_type(run_backed_item)
        || !get_manual_item_type(item)
        || get_manual_item_type(item) === get_manual_item_type(run_backed_item)
      )
    )) || null
    : null;
  const fallback_support_item = items.find((item) => item !== run_backed_item && manual_item_has_review_shape(item)) || null;
  const support_item = matching_support_item || fallback_support_item || null;

  return merge_manual_items(
    run_backed_item || items.find((item) => manual_item_has_review_shape(item)) || items[0] || null,
    support_item
  );
}

export const manageVerificationComputed = {
  verification_workspace_mode() {
    return resolve_verification_workspace_mode(this.$route);
  },
  is_manual_page() {
    return this.verification_workspace_mode === 'manual';
  },
  is_field_verification_page() {
    return this.verification_workspace_mode === 'field-verification';
  },
  uses_manual_style_workspace() {
    return this.is_manual_page || this.is_field_verification_page;
  },
  is_entity_verification_read_only() {
    const role = normalize_role_value(this.$store.getters.currentRole || get_session_role());
    return ENTITY_ROLE_VALUES.has(role);
  },
  is_detail_page() {
    return ['OnboardingVerificationCase', 'OnboardingManualVerificationCase'].includes(this.$route.name);
  },
  detail_application_id() {
    return Number(this.$route.params.applicationId || 0);
  },
  active_journey_filter() {
    return resolve_onboarding_journey_filter_key(this.$route);
  },
  filtered_checks() {
    return filter_verification_checks(this.checks, this.verification_workspace_mode, this.active_filter);
  },
  case_rows() {
    const rows = [];
    const rows_by_key = new Map();

    this.filtered_checks.forEach((check_item) => {
      const case_item = check_item?.case_item || {};
      const row_key = String(case_item?.application_id || case_item?.id || case_item?.entity_profile_id || check_item?.id || rows.length);

      if (!rows_by_key.has(row_key)) {
        const next_row = create_case_row(row_key, case_item, check_item);
        rows_by_key.set(row_key, next_row);
        rows.push(next_row);
      }

      apply_check_status_to_row(rows_by_key.get(row_key), check_item);
    });

    return rows;
  },
  table_total_rows() {
    return this.filtered_case_rows.length;
  },
  filtered_case_rows() {
    return this.filter_table_rows(this.case_rows);
  },
  paginated_case_rows() {
    return this.paginate_table_rows(this.filtered_case_rows);
  },
  ...tablePaginationComputed,
  selected_case_checks() {
    if (!this.selected_case) return [];

      const matched_checks = this.checks
        .filter((item) => this.matches_case(item?.case_item, this.selected_case))
        .filter((item) => (
        this.uses_manual_style_workspace
          ? item?.verification_mode === 'manual'
          : item?.verification_mode !== 'manual'
        ));
    const matched_non_placeholder_checks = matched_checks.filter((item) => !item?.is_gate_placeholder);
    const selected_case_bound_auto_checks = build_selected_case_bound_auto_checks(this.selected_case);
    const grouped_checks = new Map();

    if (matched_non_placeholder_checks.length) {
      matched_non_placeholder_checks.forEach((item) => {
        merge_selected_check_group(grouped_checks, item);
      });
    } else if (!this.uses_manual_style_workspace && selected_case_bound_auto_checks.length) {
      selected_case_bound_auto_checks.forEach((item) => {
        merge_selected_check_group(grouped_checks, item);
      });
    } else {
      matched_checks.forEach((item) => {
        merge_selected_check_group(grouped_checks, item);
      });
    }

    return Array.from(grouped_checks.values()).sort((left, right) => (
      get_check_sort_value(right) - get_check_sort_value(left)
    ));
  },
  selected_case_all_checks() {
    if (!this.selected_case) return [];

    return this.checks
      .filter((item) => this.matches_case(item?.case_item, this.selected_case))
      .sort((left, right) => get_check_sort_value(right) - get_check_sort_value(left));
  },
  detail_current_gate_id() {
    if (this.is_manual_page) return 'manual-verification';
    if (this.is_field_verification_page) return 'field-verification';
    return 'verification';
  },
  traversed_workflow_gate_ids() {
    return build_traversed_workflow_gate_ids(this.workflow_transition_history);
  },
  case_journey_steps() {
    if (!this.selected_case) return [];

    const steps = this.filter_case_journey_steps(
      build_case_gate_journey(this.selected_case, this.detail_current_gate_id),
      this.detail_current_gate_id
    );
    const current_index = steps.findIndex((step) => (
      String(step?.gate_id || '').trim().toLowerCase() === String(this.detail_current_gate_id || '').trim().toLowerCase()
    ));
    if (current_index < 0) {
      return steps;
    }

    const traversed_gate_ids = new Set(this.traversed_workflow_gate_ids);
    return steps.map((step, index) => ({
      ...step,
      state: index === current_index
        ? 'current'
        : index < current_index && (
          String(step?.state || '').trim().toLowerCase() === 'completed'
          || traversed_gate_ids.has(step.gate_id)
        )
          ? 'completed'
          : 'upcoming'
    }));
  },
  previous_reviewable_gate() {
    const current_index = this.case_journey_steps.findIndex((step) => step.state === 'current');
    if (current_index <= 0) return null;
    return this.case_journey_steps
      .slice(0, current_index)
      .filter((step) => step.state === 'completed')
      .slice(-1)[0] || null;
  },
  previous_gate_tab() {
    if (!this.previous_reviewable_gate) return null;

    return {
      id: `history-${this.previous_reviewable_gate.gate_id}`,
      gate_id: this.previous_reviewable_gate.gate_id,
      label: this.previous_reviewable_gate.label,
      badge: 'Completed'
    };
  },
  historical_gate_tabs() {
    const current_index = this.case_journey_steps.findIndex((step) => step.state === 'current');
    if (current_index <= 0) return [];

    return this.case_journey_steps
      .slice(0, current_index)
      .filter((step) => step.state === 'completed')
      .map((step) => ({
        id: `history-${step.gate_id}`,
        gate_id: step.gate_id,
        label: step.label,
        badge: 'Completed'
      }));
  },
  manual_history_gate_tab() {
    return this.historical_gate_tabs.find((tab) => tab.gate_id === 'manual-verification') || null;
  },
  active_manual_historical_gate_tab() {
    return this.historical_gate_tabs.find((tab) => tab.id === this.active_manual_section) || null;
  },
  active_manual_historical_transition_log() {
    return get_gate_transition_log(this.workflow_transition_history, this.active_manual_historical_gate_tab?.gate_id);
  },
  active_manual_historical_gate_status_label() {
    const tab = this.active_manual_historical_gate_tab;
    const log = this.active_manual_historical_transition_log;
    if (!tab) return 'Completed';

    return String(
      log?.to_status?.name
      || log?.action_taken
      || tab.label
      || 'Completed'
    ).trim() || 'Completed';
  },
  active_manual_historical_gate_status_tone() {
    const label = String(this.active_manual_historical_gate_status_label || '').trim().toLowerCase();
    if (label.includes('reject')) return 'danger';
    if (label.includes('request') || label.includes('change')) return 'warning';
    if (label.includes('approve') || label.includes('complete') || label.includes('submit')) return 'success';
    return 'secondary';
  },
  active_manual_historical_gate_actor() {
    const log = this.active_manual_historical_transition_log;
    return String(log?.reviewer || log?.role || 'System').trim() || 'System';
  },
  active_manual_historical_gate_completed_at() {
    const log = this.active_manual_historical_transition_log;
    return format_history_date(log?.date_created || '-');
  },
  active_manual_historical_gate_reason() {
    const log = this.active_manual_historical_transition_log;
    return String(log?.note || 'No additional history was recorded for this completed step.').trim();
  },
  active_manual_historical_gate_status_message() {
    const gate_id = String(this.active_manual_historical_gate_tab?.gate_id || '').trim().toLowerCase();
    if (gate_id === 'draft') return 'Application drafted.';
    if (gate_id === 'submitted') return 'Application submitted.';
    if (gate_id === 'approved') return 'Application approved.';
    if (gate_id === 'rejected') return 'Application rejected.';
    return `${this.active_manual_historical_gate_tab?.label || 'This step'} completed.`;
  },
  active_historical_gate_tab() {
    return this.historical_gate_tabs.find((tab) => tab.id === this.active_auto_section) || null;
  },
  active_historical_transition_log() {
    return get_gate_transition_log(this.workflow_transition_history, this.active_historical_gate_tab?.gate_id);
  },
  active_historical_gate_status_label() {
    const tab = this.active_historical_gate_tab;
    const log = this.active_historical_transition_log;
    if (!tab) return 'Completed';

    return String(
      log?.to_status?.name
      || log?.action_taken
      || tab.label
      || 'Completed'
    ).trim() || 'Completed';
  },
  active_historical_gate_status_tone() {
    const label = String(this.active_historical_gate_status_label || '').trim().toLowerCase();
    if (label.includes('reject')) return 'danger';
    if (label.includes('request') || label.includes('change')) return 'warning';
    if (label.includes('approve') || label.includes('complete') || label.includes('submit')) return 'success';
    return 'secondary';
  },
  active_historical_gate_actor() {
    const log = this.active_historical_transition_log;
    return String(log?.reviewer || log?.role || 'System').trim() || 'System';
  },
  active_historical_gate_completed_at() {
    const log = this.active_historical_transition_log;
    return format_history_date(log?.date_created || '-');
  },
  active_historical_gate_reason() {
    const log = this.active_historical_transition_log;
    return String(log?.note || 'No additional history was recorded for this completed step.').trim();
  },
  active_historical_gate_status_message() {
    const gate_id = String(this.active_historical_gate_tab?.gate_id || '').trim().toLowerCase();
    if (gate_id === 'draft') return 'Application drafted.';
    if (gate_id === 'submitted') return 'Application submitted.';
    if (gate_id === 'approved') return 'Application approved.';
    if (gate_id === 'rejected') return 'Application rejected.';
    return `${this.active_historical_gate_tab?.label || 'This step'} completed.`;
  },
  manual_workspace_item() {
    const manual_items = this.selected_case_all_checks.filter((item) => item?.verification_mode === 'manual');
    return build_manual_workspace_item(manual_items);
  },
  manual_workspace_badge_label() {
    return manual_stage_label_from_case(this.selected_case || {});
  },
  automatic_verification_result_cards() {
    if (!this.selected_case) {
      return [];
    }

    const matched_auto_checks = this.checks
      .filter((item) => matches_selected_case(item, this.selected_case, this.matches_case))
      .filter((item) => item?.verification_mode !== 'manual');

    return build_grouped_automatic_result_cards(
      matched_auto_checks,
      (item) => this.get_automatic_result_summary(item)
    );
  },
  manual_section_tabs() {
    const tabs = [
      { id: 'manual-review-checklist', label: this.is_field_verification_page ? 'Required Action: Field Review' : 'Required Action: Manual Review', badge: 'Current' },
      { id: 'manual-applicant-information', label: 'Application Reference', badge: 'Reference' }
    ];

    if (this.historical_gate_tabs.length) {
      tabs.push(...this.historical_gate_tabs);
    }

    tabs.push({ id: 'manual-documents', label: 'Supporting Documents' });

    if (!this.is_field_verification_page && this.is_bank_statement_manual_workspace()) {
      tabs.push({ id: 'manual-verification-results', label: 'Bank Statement Analysis' });
    }

    return tabs;
  },
  manual_workspace_tabs() {
    const workspace_tab_labels = {
      'manual-review-checklist': 'Required Action',
      'manual-documents': 'Supporting Documents',
      'manual-verification-results': 'Bank Statement Analysis'
    };

    return this.manual_section_tabs
      .filter((tab) => Object.hasOwn(workspace_tab_labels, tab.id))
      .map((tab) => ({
        ...tab,
        workspace_label: workspace_tab_labels[tab.id]
      }));
  },
  manual_history_only_tabs() {
    return this.manual_case_history_tabs.filter((tab) => tab.sidebar_state === 'completed');
  },
  manual_shell_tabs() {
    return [
      { id: 'action', label: 'Required Action', badge: 'Current' },
      { id: 'reference', label: 'Application Reference', badge: null },
      { id: 'history', label: 'Onboarding History', badge: this.manual_history_only_tabs.length ? String(this.manual_history_only_tabs.length) : null }
    ];
  },
  active_manual_shell_tab() {
    if (this.active_manual_section === 'manual-applicant-information') {
      return 'reference';
    }

    if (this.active_manual_section === 'manual-case-history' || this.active_manual_historical_gate_tab) {
      return 'history';
    }

    return 'action';
  },
  auto_section_tabs() {
    const tabs = [
      { id: 'auto-verification-results', label: 'Required Action: Auto Verification', badge: 'Current' },
      { id: 'auto-applicant-details', label: 'Application Reference', badge: 'Reference' },
      ...this.historical_gate_tabs
    ];
    return tabs;
  },
  auto_reference_tab() {
    return this.auto_section_tabs.find((tab) => tab.id === 'auto-applicant-details') || null;
  },
  auto_current_tab() {
    return this.auto_section_tabs.find((tab) => tab.id === 'auto-verification-results') || null;
  },
  active_auto_content_tab() {
    return this.auto_section_tabs.find((tab) => tab.id === this.active_auto_section) || this.auto_current_tab || null;
  },
  auto_application_reference_value() {
    const application_id = first_non_empty_text([this.selected_case?.application_id], '');
    return first_non_empty_text([
      this.selected_case?.application_reference,
      this.selected_case?.reference,
      this.selected_case?.reference_number,
      this.selected_case?.application_number,
      this.selected_case?.application_code,
      this.selected_case?.case_reference,
      application_id ? `#${application_id}` : ''
    ]);
  },
  auto_case_history_tabs() {
    const current_tab = this.auto_current_tab;
    const completed_tabs = this.historical_gate_tabs.map((tab) => {
      const journey_step = this.case_journey_steps.find((step) => step.gate_id === tab.gate_id) || null;
      const transition_log = get_gate_transition_log(this.workflow_transition_history, tab.gate_id);

      return {
        ...tab,
        sidebar_state: 'completed',
        sidebar_badge: tab.badge || 'Completed',
        step_number: journey_step?.step || null,
        actor_label: String(transition_log?.reviewer || transition_log?.role || 'System').trim() || 'System',
        timestamp_label: format_sidebar_history_date(transition_log?.date_created || ''),
        accent_label: null
      };
    });

    const current_step = this.case_journey_steps.find((step) => step.gate_id === this.detail_current_gate_id) || null;
    const current_timestamp = first_non_empty_text([
      this.selected_case?.last_run,
      this.selected_case?.updated_at,
      this.selected_case?.date_updated,
      this.selected_case?.modified_at
    ], '-');

    return [
      ...completed_tabs,
      ...(current_tab ? [{
        ...current_tab,
        label: current_step?.label || current_tab.label.replace(/^Required Action:\s*/i, ''),
        sidebar_state: 'current',
        sidebar_badge: 'In progress',
        step_number: current_step?.step || null,
        actor_label: 'Last run',
        timestamp_label: current_timestamp,
        accent_label: 'current'
      }] : [])
    ];
  },
  auto_required_action_label() {
    return this.auto_current_tab?.label || 'Required Action: Auto Verification';
  },
  auto_required_action_title() {
    return String(this.auto_required_action_label || 'Required Action')
      .replace(/^Required Action:\s*/i, '')
      .trim() || 'Current gate';
  },
  auto_required_action_sidebar_copy() {
    return 'Run or review automated verification checks, inspect returned matches or mismatches, and confirm the verification outcome.';
  },
  auto_history_only_tabs() {
    return this.auto_case_history_tabs.filter((tab) => tab.sidebar_state === 'completed');
  },
  auto_shell_tabs() {
    return [
      { id: 'action', label: 'Required Action', badge: 'Current' },
      { id: 'reference', label: 'Application Reference', badge: null },
      { id: 'history', label: 'Onboarding History', badge: this.auto_history_only_tabs.length ? String(this.auto_history_only_tabs.length) : null }
    ];
  },
  active_auto_shell_tab() {
    if (this.active_auto_section === 'auto-applicant-details') {
      return 'reference';
    }

    if (this.active_auto_section === 'auto-case-history' || this.active_historical_gate_tab) {
      return 'history';
    }

    return 'action';
  },
  manual_reference_tab() {
    return this.manual_section_tabs.find((tab) => tab.id === 'manual-applicant-information') || null;
  },
  manual_current_tab() {
    return this.manual_section_tabs.find((tab) => tab.id === 'manual-review-checklist') || null;
  },
  active_manual_content_tab() {
    return this.manual_section_tabs.find((tab) => tab.id === this.active_manual_section) || this.manual_current_tab || null;
  },
  manual_application_reference_value() {
    const application_id = first_non_empty_text([this.selected_case?.application_id], '');
    return first_non_empty_text([
      this.selected_case?.application_reference,
      this.selected_case?.reference,
      this.selected_case?.reference_number,
      this.selected_case?.application_number,
      this.selected_case?.application_code,
      this.selected_case?.case_reference,
      application_id ? `#${application_id}` : ''
    ]);
  },
  manual_case_history_tabs() {
    const current_tab = this.manual_current_tab;
    const historical_tabs = this.historical_gate_tabs.map((tab) => {
      const journey_step = this.case_journey_steps.find((step) => step.gate_id === tab.gate_id) || null;
      const transition_log = get_gate_transition_log(this.workflow_transition_history, tab.gate_id);

      return {
        ...tab,
        sidebar_state: 'completed',
        sidebar_badge: tab.badge || 'Completed',
        step_number: journey_step?.step || null,
        actor_label: String(transition_log?.reviewer || transition_log?.role || 'System').trim() || 'System',
        timestamp_label: format_sidebar_history_date(transition_log?.date_created || ''),
        accent_label: null
      };
    });

    const current_step = this.case_journey_steps.find((step) => step.gate_id === this.detail_current_gate_id) || null;
    const current_timestamp = first_non_empty_text([
      this.selected_case?.last_run,
      this.selected_case?.updated_at,
      this.selected_case?.date_updated,
      this.selected_case?.modified_at
    ], '-');

    return [
      ...historical_tabs,
      ...(current_tab ? [{
        ...current_tab,
        label: current_step?.label || current_tab.label.replace(/^Required Action:\s*/i, ''),
        sidebar_state: 'current',
        sidebar_badge: 'In progress',
        step_number: current_step?.step || null,
        actor_label: 'Last run',
        timestamp_label: current_timestamp,
        accent_label: 'current'
      }] : []),
    ];
  },
  manual_required_action_label() {
    return this.manual_current_tab?.label || (this.is_field_verification_page ? 'Required Action: Field Review' : 'Required Action: Manual Review');
  },
  manual_required_action_title() {
    return String(this.manual_required_action_label || 'Required Action')
      .replace(/^Required Action:\s*/i, '')
      .trim() || 'Current gate';
  },
  manual_required_action_sidebar_copy() {
    if (this.is_field_verification_page) {
      return 'Compare submitted answers against supporting evidence field by field and capture the verification result for each item.';
    }

    return 'Review supporting evidence, complete the manual verification checklist, and record the final verification decision.';
  },
  manual_workspace_documents() {
    return build_manual_workspace_documents(this.selected_case, get_manual_document_status);
  },
  manual_workspace_activity_log() {
    const timeline_entries = build_workflow_activity_entries(this.selected_case);
    const verification_entries = build_verification_activity_entries(this.selected_case_checks, this.get_check_history);
    return [...timeline_entries, ...verification_entries]
      .filter((entry) => entry?.title)
      .sort((left, right) => String(right?.timestamp || '').localeCompare(String(left?.timestamp || '')));
  },
  manual_review_progress() {
  const questions = collect_review_questions(this.get_manual_review_form_sections());
  const reviewed_runs = this.get_manual_field_runs(this.manual_review_modal?.item);

  const reviewed_question_ids = new Set(
    reviewed_runs
      .map((field_run) => Number(field_run?.section_question || 0))
      .filter((question_id) => question_id > 0)
  );

  const total_questions = questions.length;

  const reviewed_questions = questions.filter((question) =>
    reviewed_question_ids.has(Number(question?.id || 0))
  ).length;

  const remaining_questions = Math.max(
    total_questions - reviewed_questions,
    0
  );

  const completion_percentage = total_questions
    ? Math.round((reviewed_questions / total_questions) * 100)
    : 0;

  const matched_count = reviewed_runs.filter(
    (field_run) =>
      this.normalize_match_status(field_run?.match_status) === 'match'
  ).length;

  const mismatched_count = reviewed_runs.filter(
    (field_run) =>
      this.normalize_match_status(field_run?.match_status) === 'mismatch'
  ).length;

  const partial_count = reviewed_runs.filter(
    (field_run) =>
      this.normalize_match_status(field_run?.match_status) === 'partial'
  ).length;

  const unable_to_verify_count = reviewed_runs.filter(
    (field_run) =>
      this.normalize_match_status(field_run?.match_status) === 'unable_to_verify'
  ).length;

  const progress = {
    total_questions,
    reviewed_questions,
    remaining_questions,
    completion_percentage,
    matched_count,
    mismatched_count,
    partial_count,
    unable_to_verify_count
  };

  return progress;
},
  active_summary_form_section() {
    const forms = Array.isArray(this.summary_case_form_sections) ? this.summary_case_form_sections : [];
    for (const form of forms) {
      const matched_section = (Array.isArray(form?.sections) ? form.sections : [])
        .find((section) => this.summary_tab_id_for_section(form, section) === this.active_summary_detail_tab);

      if (matched_section) return matched_section;
    }

    return null;
  }
};
